import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Sparkles, Check, X as XIcon, ArrowRight } from "lucide-react";
import { BQ } from "../../lib/bharatQuest";

export default function RegionMap({ user }) {
  const [regions, setRegions] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { BQ.regions().then((d) => setRegions(d.regions)); }, []);

  const isUnlocked = (slug) => user.regions_unlocked.includes(slug);

  return (
    <div data-testid="bq-region-map">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Civilization Map</div>
      <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6] max-w-2xl">
        Unlock <span className="mythos-gold">eight</span> civilizational regions.
      </h2>
      <p className="text-[#E8EBF0]/65 mt-2 max-w-2xl">Earn XP to unlock new regions. Each holds its own history, science, and stories.</p>

      {/* Stylized node map */}
      <div className="relative mt-10 vault-card rounded-2xl overflow-hidden" style={{ aspectRatio: "16/9", maxWidth: "100%" }} data-testid="bq-map-canvas">
        {/* Subtle background grid */}
        <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" preserveAspectRatio="none">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#C9A66B" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Connecting lines between regions */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          {regions.slice(0, -1).map((r, i) => {
            const next = regions[i + 1];
            return (
              <line key={i}
                x1={r.x} y1={r.y} x2={next.x} y2={next.y}
                stroke="#C9A66B" strokeWidth="0.15" strokeDasharray="0.5 0.8" opacity="0.4"
              />
            );
          })}
        </svg>

        {/* Region nodes */}
        {regions.map((r, i) => {
          const unlocked = isUnlocked(r.slug);
          return (
            <motion.button
              key={r.slug}
              onClick={() => setActive(r)}
              data-testid={`bq-region-${r.slug}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
              style={{ left: `${r.x}%`, top: `${r.y}%` }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: i * 0.08, type: "spring", stiffness: 200 }}
              whileHover={{ scale: 1.15 }}
            >
              <div className={`relative w-14 h-14 md:w-16 md:h-16 rounded-full grid place-items-center transition-all ${
                unlocked
                  ? "bg-[#0F1623] border-2 border-[#C9A66B] shadow-[0_0_24px_rgba(201,166,107,0.4)]"
                  : "bg-[#1A2436] border-2 border-[#3A3D44] opacity-60"
              }`}>
                {unlocked ? (
                  <Sparkles className="mythos-gold" size={20} />
                ) : (
                  <Lock className="text-[#7d8294]" size={18} />
                )}
                {unlocked && (
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-[#C9A66B]"
                    animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                  />
                )}
              </div>
              <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] tracking-[0.14em] uppercase font-bold text-[#F4EFE6] opacity-0 group-hover:opacity-100 transition pointer-events-none">
                {r.name}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Region list cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="bq-region-list">
        {regions.map((r) => {
          const unlocked = isUnlocked(r.slug);
          return (
            <button
              key={r.slug}
              onClick={() => setActive(r)}
              data-testid={`bq-region-card-${r.slug}`}
              className={`text-left vault-card rounded-xl p-5 transition border-2 ${
                unlocked
                  ? "border-[#2A3349] hover:border-[#C9A66B]"
                  : "border-[#2A3349] opacity-65"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: r.color + "33", color: r.color }}>
                  {unlocked ? <Sparkles size={16} /> : <Lock size={14} />}
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: unlocked ? "#C9A66B" : "#7d8294" }}>
                  {unlocked ? "Unlocked" : `${r.unlock_xp} XP`}
                </div>
              </div>
              <div className="font-mythos font-bold text-[#F4EFE6]">{r.name}</div>
              <div className="text-[11px] text-[#E8EBF0]/55 mt-1">{r.era}</div>
            </button>
          );
        })}
      </div>

      {active && <RegionDetailModal region={active} unlocked={isUnlocked(active.slug)} onClose={() => setActive(null)} />}
    </div>
  );
}

function RegionDetailModal({ region, unlocked, onClose }) {
  return (
    <motion.div
      className="fixed inset-0 z-50 bg-[#0E0F12]/85 backdrop-blur-sm flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      onClick={onClose}
      data-testid="bq-region-modal"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="vault-card rounded-2xl p-7 md:p-9 max-w-2xl w-full border border-[#2A3349]"
      >
        <button onClick={onClose} className="float-right text-[#7d8294] hover:text-[#F4EFE6]" data-testid="bq-region-modal-close">
          <XIcon size={20} />
        </button>
        <div className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold mb-2">{region.era}</div>
        <h3 className="font-mythos text-3xl font-extrabold text-[#F4EFE6]">{region.name}</h3>
        <p className="text-[#E8EBF0]/75 mt-4 leading-relaxed">{region.intro}</p>
        {!unlocked ? (
          <div className="mt-6 inline-flex items-center gap-2 vault-amber-bg text-[#0F1623] rounded-full px-4 py-2 text-xs font-bold">
            <Lock size={12} /> Unlocks at {region.unlock_xp} XP
          </div>
        ) : (
          <>
            <div className="text-[11px] tracking-[0.18em] uppercase mythos-gold font-bold mt-7 mb-3">Highlights</div>
            <ul className="space-y-2">
              {region.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-[#E8EBF0]/80">
                  <Check size={14} className="mythos-gold mt-1 flex-shrink-0" />
                  <span className="text-sm">{h}</span>
                </li>
              ))}
            </ul>
            <a href="/stories" className="mt-7 inline-flex items-center gap-2 mythos-gold-bg text-[#0E0F12] rounded-full px-5 py-2.5 font-semibold hover:opacity-90 transition" data-testid="bq-region-explore">
              Explore stories <ArrowRight size={14} />
            </a>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
